import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useLazyQuery } from '@apollo/client';

import { CAN_PAY_DRAFT } from '../../../../../api';
import { CALCS_PER_PAGE, CALC_NAMES, CALC_PAGE_TYPES, CALC_TYPES } from 'utils/calculatorUtils';
import { LoaderOn, Loader } from 'tt-ui-lib/core';
import CalcAccordion from '../../../components/CalcAccordion/CalcAccordion';
import CalcIcon from '../../../components/CalcIcon/CalcIcon';
import CalcFilter from '../../../components/CalcFilter/CalcFilter';
import CalcDraft from '../../../components/CalcDraft/CalcDraft';
import CalcEmptyListBlock from '../../../components/CalcEmptyListBlock/CalcEmptyListBlock';
import { Alert, Snackbar } from '@mui/material';
import { getCalcDrafts } from 'api/calc/endpoints/calc';
import { useSelectedContext } from 'context/contextSelector';

const benchmarkTypes = [
  [CALC_TYPES.CARBON, CALC_TYPES.PROJECT],
  [CALC_TYPES.LOCAL],
  [CALC_TYPES.GLOBAL],
];

const Drafts = ({ targetWidth }) => {
  const pageType = CALC_PAGE_TYPES.BENCHMARK;
  const types = CALCS_PER_PAGE[pageType];
  const { pathname } = useLocation();

  const [filteredDrafts, setFilteredDrafts] = useState([]);
  const [companyDrafts, setCompanyDrafts] = useState([]);
  const [isPayChecked, setIsPayChecked] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [rawDrafts, setRawDrafts] = useState([]);
  const [canPayDraft] = useLazyQuery(CAN_PAY_DRAFT);
  const [needReload, setNeedReload] = useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [loading, setLoading] = useState(true);

  const { selectedCompany: widjetCompany } = useSelectedContext((context) => context.user);

  const onErrorClose = () => {
    setErrorText(null);
  };

  const callReload = () => {
    setNeedReload(true);
    setIsPayChecked(false);
  };

  const RenewCalcDraftsList = () => {
    setLoading(true);
    getCalcDrafts()
      .then((res) => {
        const { data, success } = res.data;
        if (success) {
          setRawDrafts(data);
          setIsPayChecked(false);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        setNeedReload(false);
      });
  };

  useEffect(() => {
    if (!needReload) return;
    RenewCalcDraftsList();
  }, [needReload]);

  useEffect(() => {
    RenewCalcDraftsList();
  }, [pathname]);

  useEffect(() => {
    if (isPayChecked || !rawDrafts.length) return;
    canPayDraft({
      variables: { input: { calculatorIds: [...drafts.map((d) => d.id)] } },
    })
      .then((res) => {
        const { data } = res;
        const canPayList = data?.getCalculatorsPayAccess;
        if (!canPayList) return;
        const newDrafts = rawDrafts.map((d) => ({
          ...d,
          canPay: canPayList.find((p) => p.id === d.id)?.canPay ?? true,
        }));
        setDrafts([...newDrafts]);
        setIsPayChecked(true);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [rawDrafts, isPayChecked]);

  useEffect(() => {
    const newCompanyDrafts =
      widjetCompany && widjetCompany.selectedCompany
        ? drafts.filter((el) => el.companyId === widjetCompany.selectedCompany.id)
        : drafts;
    setCompanyDrafts(newCompanyDrafts);
  }, [drafts, widjetCompany]);

  return (
    <>
      {companyDrafts.length > 0 ? (
        <>
          <CalcFilter setSearchedItems={setFilteredDrafts} items={companyDrafts} />
          {benchmarkTypes.map((arr) => (
            <>
              {filteredDrafts.some((d) => arr.includes(d.type)) && (
                <div key={arr[0]} style={{ position: 'relative' }}>
                  <CalcAccordion logo={<CalcIcon type={arr[0]} />} title={CALC_NAMES[arr[0]]}>
                    {filteredDrafts
                      .filter((draft) => arr.includes(draft.type))
                      .map((draft) => (
                        <div key={draft.id}>
                          <CalcDraft
                            targetWidth={targetWidth}
                            draft={draft}
                            refresh={callReload}
                            setErrorText={setErrorText}
                          />
                        </div>
                      ))}
                  </CalcAccordion>
                  {needReload && <LoaderOn />}
                </div>
              )}
            </>
          ))}
        </>
      ) : loading ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Loader />
        </div>
      ) : (
        <CalcEmptyListBlock collectionName="drafts" />
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!errorText}
        onClose={onErrorClose}
      >
        <Alert
          onClose={onErrorClose}
          severity="error"
          sx={{ width: '100%', backgroundColor: '#D32F2F', color: '#ffffff' }}
        >
          {errorText ?? 'Something went wrong, try again'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Drafts;
